import { useState } from 'react';

import useAttachmentVariant from '../../hooks/useAttachmentVariant';
import { formatBytes } from '../../utils/formatters';
import { showsAsPicture } from '../../utils/fileValues';
import { getFileTypeIcon } from './AttachmentsSection';

/**
 * The file chip (docs/plans/data-types-file-element.md §6): the type icon
 * from getFileTypeIcon, the name ellipsised, and — in the read view — the
 * size. A file is NOT a record, so this is not a record badge: no identity
 * tile, no record color, never a photo inside it. Sizes `xs` (a table cell),
 * `sm` (a tile, dense) and the read view's default.
 */
export function FileChip({ file, size = 'md', showSize = false, className = '' }) {
	const TypeIcon = getFileTypeIcon(file?.ext);
	const name = file?.originalName || 'File';
	const bytes = showSize && Number.isFinite(file?.size) ? formatBytes(file.size) : '';
	return (
		<span className={`file-chip file-chip--${size}${className ? ` ${className}` : ''}`} title={name} data-testid="file-chip">
			<TypeIcon className="file-chip__icon" aria-hidden="true" />
			<span className="file-chip__name">{name}</span>
			{bytes ? <span className="file-chip__size">{bytes}</span> : null}
		</span>
	);
}

/**
 * One file as a picture: the server's `sm` (160 px square) or `md` (800 px)
 * WebP, fetched through 10a's variant cache with the JWT (never the
 * original, never a ledger row). While it loads, and when the fetch fails or
 * the bytes will not decode, it falls back to the chip — never a broken
 * image. `itemId` is the item the file is bound to: a bound file is its own
 * item's attachment, so the item variant route serves it. A file the item
 * form has uploaded but not saved has no item yet: `heldIn` (its collection)
 * reads it through the held route instead (PR 10b).
 */
export function FilePicture({ itemId, heldIn = null, file, size, className = '', chipSize = 'sm' }) {
	const variant = useAttachmentVariant(itemId, showsAsPicture(file, size) ? file : null, size, { heldIn });
	const [broken, setBroken] = useState(false);
	if (!showsAsPicture(file, size) || variant.failed || broken) {
		return <FileChip file={file} size={chipSize} />;
	}
	return (
		<span className={`file-picture file-picture--${size}${className ? ` ${className}` : ''}`} data-testid="file-picture">
			{variant.url ? (
				<img
					className="file-picture__img"
					src={variant.url}
					alt={file?.originalName || ''}
					draggable={false}
					onError={() => setBroken(true)}
				/>
			) : null}
		</span>
	);
}
